<template>
    <AppBreadcrumbs
        :base-url="runtimeConfig.public.BASE_URL"
        :parents="[
            {
                name: config.brand_name,
                full_slug: '',
            },
        ]"
        :current="{
            name: story?.name || 'Authors',
            full_slug: 'authors',
        }"
    />
    <div class="authors-overview">
        <h1 class="authors-overview__title">
            Our <span>Authors</span>
        </h1>

        <div class="authors-overview__list">
            <div
                v-for="author in authors"
                :key="author.uuid"
                class="authors-overview-item"
            >
                <div class="authors-overview-item__top">
                    <NuxtImg
                        width="64"
                        height="64"
                        fit="cover"
                        format="auto"
                        loading="lazy"
                        :src="author.content.image.filename"
                        :alt="author.content.image.alt"
                        class="authors-overview-item__image"
                    />
                    <h2 class="authors-overview-item__full-name">
                        <nuxt-link :to="`/${author.full_slug}`">
                            {{ author.content.first_name }} <span>{{ author.content.last_name }}</span>
                        </nuxt-link>
                    </h2>
                </div>
                <p class="authors-overview-item__description">
                    {{ author.content.description }}
                </p>
                <nuxt-link
                    :to="`/${author.full_slug}`"
                    class="authors-overview-item__link"
                >
                    Read more
                </nuxt-link>
            </div>
        </div>
    </div>
</template>

<script setup>
// The `authors` story only carries the meta tags and breadcrumb label, so the
// overview still renders without it. The author list itself is what matters.
const { data: story } = await useAsyncData('authors-story', () => $fetch(`/api/page?full_slug=authors`))

const config = useState('config')
const runtimeConfig = useRuntimeConfig()

const { data: authors } = await useAsyncData('authors', () => $fetch(`/api/authors`))

if (!authors.value?.length) {
    throw createError({ statusCode: 404, statusMessage: 'No authors found', fatal: true })
}

const metaDescription = story.value?.content?.meta_description

useHead({
    title: story.value?.content?.meta_title || 'Authors',
    meta: metaDescription
        ? [
                {
                    hid: 'description',
                    name: 'description',
                    content: metaDescription,
                },
            ]
        : [],
})
</script>

<style lang="scss">
@use '@vemedia/bcn-components/scss/responsive' as *;

.authors-overview {
    margin: var(--space-4) auto;

    @include desktop {
        margin: var(--space-6) auto;
    }

    &__title {
        font-weight: 600;
        font-size: 2.5rem;
        margin-bottom: 1rem;

        span {
            color: var(--primary);
        }
    }

    &__list {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        gap: 30px;

        @include tablet {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    &-item {
        display: flex;
        flex-direction: column;
        padding: 16px;
        border-radius: 10px;
        border: 1px solid #cccccc;

        &__top {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 12px;
        }

        &__image {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            object-fit: cover;
            object-position: center;
        }

        &__full-name {
            font-weight: 600;
            font-size: 1.125rem;

            a {
                color: inherit;

                &:hover {
                    text-decoration: underline;
                }
            }

            span {
                color: var(--primary);
            }
        }

        &__description {
            flex-grow: 1;
        }

        &__link {
            display: flex;
            align-items: center;
            height: 32px;
            width: fit-content;
            margin-top: 0.75rem;
            border-radius: 16px;
            background-color: var(--primary-btn);
            padding: 0 16px;
            font-weight: 600;
            font-size: 0.875rem;
            color: #ffffff;
        }
    }
}
</style>
